<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ButtonType } from '@/enums/ButtonType.enum';

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'CButton',
	props: {
		/**
		 * Type of button - will define css class for button in normal & active state
		 *
		 * class for
		 *  - normal state: `c-button-${type}` (eg. c-button-filter)
		 *  - active state: `c-button-${type}--active` (eg. c-button-filter--active)
		 *
		 *  If you are adding new type, add it in ButtonType enum,
		 *  then add proper style for normal & active state in this component.
		 */
		type: {
			required: false,
			type: String as PropType<ButtonType>,
			default: () => ButtonType.PRIMARY,
			validator: (value: ButtonType) => Object.values(ButtonType).includes(value),
		},
		elementType: {
			required: false,
			type: String as PropType<"button" | "reset" | "submit">,
			default: 'button',
		},
		/**
		 *  Is button active - will add proper active class for current type
		 *  eg.
		 *  type: 'filter'
		 *  active state class: 'c-button-filter--active'
		 */
		isActive: {
			required: false,
			type: Boolean,
			default: false,
		},
		title: {
			required: false,
			type: String,
			default: '',
		},
		disabled: {
			required: false,
			type: Boolean,
			default: false,
		},
		disabledTitle: {
			required: false,
			type: String,
			default: '',
		},
	},
	computed: {
		activeClass(): string {
			return `c-button-${this.type}--active`;
		},
		disabledClass(): string {
			return `c-button-${this.type}--disabled`;
		},
	},
});
</script>

<template>
	<button
		v-wave="!disabled"
		:class="[
			'c-button',
			`c-button-${type}`,
			{ [activeClass]: isActive },
			{ [disabledClass]: disabled },
		]"
		:title="disabled ? disabledTitle : title"
		:disabled="disabled"
		:type="elementType"
	>
		<slot />
	</button>
</template>

<style scoped lang="scss">
.c-button {
  @include tools.font-size(16px, 24px);
	cursor: pointer;
	padding: 6px 12px;
	border-radius: 6px;
	border: 1px solid tokens.$color-blue;
  background: #F8F9FA;
	color: black;
	white-space: pre-wrap;
}

.c-button--disabled {
	cursor: not-allowed;
	filter: grayscale(35%);
	opacity: 0.75;

	&:hover {
		filter: grayscale(35%);
	}
}

.c-button-primary {
	border: 1px solid tokens.$color-blue;
	background: tokens.$color-blue;
	color: tokens.$color-white;
}
.c-button-primary--disabled {
	@extend .c-button--disabled;
}

.c-button-filter {
	border: 1px solid #212529;
	background: transparent;
	color: #212529;

  &:hover {
		background: #212529;
    border-color: #212529;
    color: white;
	}
}
.c-button-filter--active {
  background: #212529;
  color: white;
}
.c-button-filter--disabled {
	@extend .c-button--disabled;
}

.c-button-tab {
	border: 1px solid tokens.$color-gray-f1;
	background: tokens.$color-gray-f1;
	color: tokens.$color-gray-89;
}
.c-button-tab--active {
	border: 1px solid tokens.$color-pastel-blue;
	background: tokens.$color-pastel-blue;
	color: tokens.$color-blue;
}
.c-button-tab--disabled {
	@extend .c-button--disabled;
}

.c-button-nav {
	border: 1px solid tokens.$color-pastel-dark-blue;
	background: tokens.$color-pastel-dark-blue;
	color: tokens.$color-gray-fa;
}
.c-button-nav--active {
	border: 1px solid tokens.$color-blue;
	background: tokens.$color-pastel-dark-blue;
	color: tokens.$color-gray-fa;
}
.c-button-nav--disabled {
	@extend .c-button--disabled;
}

.c-button-link {
	@include tools.size(fit-content);

	padding: 0;
	border: none;
	background: transparent;
	color: tokens.$color-white;
	text-decoration: underline;

	&:hover {
		color: tokens.$color-pastel-blue;
	}
}

.c-button-link--active {
	color: tokens.$color-pastel-blue;
}
.c-button-link--disabled {
	@extend .c-button--disabled;
}
</style>
